<template lang="pug">
    v-dialog.timerDialog(
        v-model="dialog"
        full-screen
        content-class="timerModal"
    )

        // Overlay
        div.backgroundVideoOverflow(:style="{ 'backgroundImage': `url(${timerSettings.imageUrl})` }")
            div

        transition(name="slide-y-fade")
            div.timerModalInner(v-if="timerCounter")

                div.timer

                    div.timerControls

                        button-component.mx-0(
                            v-if="timerSettings.soundUrl"
                            color="white"
                            :action="toggleBackgroundSound"
                            outline
                        )
                            span {{ muteSound ? 'Sound on' : 'Sound off' }}
                            v-icon(v-if="muteSound" color="white") volume_off
                            v-icon(v-else color="white") volume_up


                        // Run/Pause timer
                        button-component.mr-0(
                            color="white"
                            :action="toggleRunTimer"
                            outline
                        ) {{ isPaused ? $t('defaultButtons.resume') : $t('defaultButtons.pause') }}


                    // Timer circle
                    div.timerCounter
                        // Progress circle
                        radial-progress-bar(
                            :diameter="260"
                            :strokeWidth="12"
                            :innerStrokeWidth="12"
                            :completed-steps="timerCounter / 60"
                            :total-steps="timerSettings.timer"
                            :animateSpeed="2000"
                            :isClockwise="true"
                            innerStrokeColor="#ffffff"
                            startColor="#7096D2"
                            stopColor="#7096D2"
                        )

                        // Countdown
                        div.timerCountdown {{ countdown }}

                div.text-xs-center.mt-5.full-width

                    button-component.timerFinish(
                        color="action"
                        block
                        large
                        :action="finishTimer"
                    ) {{ $t('defaultButtons.finish') }}
</template>

<script>
    import VueTypes from 'vue-types';
    import Button from '~components/Button';
    import RadialProgressBar from 'vue-radial-progress';
    import sessions from '~api/sessions';
    import { mapActions, mapGetters } from 'vuex';
    import { App as CapacitorApp } from '@capacitor/app';
    import {
        TimerSchedule,
        LiveActivity,
        AndroidTimerNotification,
        HealthIntegration,
        isNative,
        isIOS
    } from '~services';

    const BELL_URL =
        'https://s3.us-east-2.amazonaws.com/chillanywhere.com/timer/sounds/386429-Meditation_Bell_-Dynamic_-Aston_Stealth_003.mp3';

    export default {
        components: {
            RadialProgressBar,
            ButtonComponent: Button
        },
        props: {
            timerSettings: VueTypes.object.def({}),
            dialogOpen: VueTypes.bool.def(false),
            timerCallback: VueTypes.func.def(() => {})
        },
        data() {
            return {
                isWatched: false,
                dialog: true,
                // Driven by `recomputeFromClock` off the wall-clock `endAt`
                // stored in TimerSchedule so backgrounding never drifts.
                timerCounter: 0,
                timeMeditated: null,
                timerInterval: null,
                soundRepeatInterval: null,
                intervalSound: null,
                backgroundSound: null,
                muteSound: false,
                isPaused: false,
                sessionViewId: null,
                // Timer-schedule bookkeeping.
                startedAt: 0,
                endAt: 0,
                pausedAt: 0,
                pausedRemaining: 0,
                appStateUnsub: null,
                // Web Audio bell scheduling — plays on the audio thread so
                // interval bells fire silently on the Lock Screen without a
                // notification banner. Requires UIBackgroundModes: audio.
                webAudioCtx: null,
                bellBuffer: null,
                scheduledBells: [],
                health: new HealthIntegration()
            };
        },

        computed: {
            ...mapGetters('auth', ['isMember']),
            ...mapGetters('sessions', ['moodBefore', 'session']),
            countdown() {
                const hours =
                    this.timerCounter >= 3600 &&
                    parseInt(this.timerCounter / 3600);

                return `${hours ? `${hours} h, ` : ''} ${
                    this.timerCounter >= 60
                        ? `${parseInt(
                              (this.timerCounter - hours * 3600) / 60
                          )} m, `
                        : ''
                } ${this.timerCounter % 60} s`;
            }
        },
        async mounted() {
            // Kill any session audio that was kept alive by the keep-alive'd
            // SessionsViewPage — otherwise its soundtrack plays on top of
            // the timer bells and background sound.
            this.stopSessionPlayer();

            const totalSeconds = this.timerSettings?.timer * 60;
            this.timerCounter = totalSeconds;
            this.startedAt = Date.now();

            // Hand timing to the OS via local notifications — reliable
            // end-bell delivery even if the app is backgrounded or the
            // screen locks. Only the final "Meditation complete" alert is
            // scheduled; interval bells come from Web Audio below so the
            // Lock Screen stays banner-free between intervals.
            const startResult = await TimerSchedule.startTimer({
                totalSeconds,
                intervalMinutes: this.timerSettings?.interval || 0,
                sound: isIOS() ? 'bell.caf' : 'bell'
            });
            this.endAt = startResult.endAt;
            const { endAt, notificationsDenied } = startResult;

            // Light up the Dynamic Island + Lock Screen countdown.
            // Native no-op if the Widget Extension isn't configured yet.
            LiveActivity.start({ endAt, startedAt: this.startedAt });

            // Android-equivalent: ongoing chronometer notification that
            // shows a live countdown on the Lock Screen. Skip silently if
            // the user denied POST_NOTIFICATIONS — the native plugin would
            // reject and we'd flood the console.
            if (!notificationsDenied) {
                AndroidTimerNotification.start({ endAt });
            }

            // Push app-icon artwork into the iOS Now Playing widget. Needs
            // a data-URL because iOS's native media session can't fetch
            // assets served from the `capacitor://` WebView scheme.
            this.setLockScreenMetadata();

            // Init stats tracking
            this.saveViewSession();

            // Foreground-only safety bell (e.g. app reopened mid-session).
            this.intervalSound = new Audio(BELL_URL);

            // Web Audio scheduling: plays each interval bell from the
            // audio thread at a precise time, so it works on the Lock
            // Screen without needing a visible local notification.
            await this.initWebAudioBell();
            this.scheduleIntervalBells(
                totalSeconds,
                this.timerSettings?.interval || 0
            );

            // Foreground tick: recompute remaining from wall clock every
            // 250ms so the UI is always truthful about elapsed time,
            // even after the OS paused/resumed our JS.
            this.initTimer();

            // When the app comes back to the foreground, re-sync the UI
            // counter from wall-clock in case JS was frozen.
            if (isNative()) {
                const handle = await CapacitorApp.addListener(
                    'appStateChange',
                    ({ isActive }) => {
                        if (isActive) this.recomputeFromClock();
                    }
                );
                this.appStateUnsub = handle;
            }

            // Background sound (selected by the user on the settings
            // page) is still an in-app Audio loop. Muted while the app
            // is backgrounded — that's an OS limitation, not a bug.
            if (this.timerSettings?.soundUrl) {
                this.initBackgroundSound();
            }
        },
        async beforeDestroy() {
            this.clearAllIntervals();
            this.cancelScheduledBells();
            this.teardownWebAudio();
            if (this.appStateUnsub) {
                try {
                    this.appStateUnsub.remove();
                } catch (_) {}
                this.appStateUnsub = null;
            }
            await TimerSchedule.cancelTimer();
            await LiveActivity.end();
            await AndroidTimerNotification.stop();

            if (!this.isWatched)
                sessions.setWatched(process.env.VUE_APP_TIMER_SESSION_ID);
        },
        methods: {
            ...mapActions('sessions', ['setShowMood']),
            ...mapActions('player', { stopSessionPlayer: 'stop' }),

            /**
             * Re-derive `timerCounter` from the stored `endAt`. Called on
             * every tick and on foreground restore.
             */
            recomputeFromClock() {
                if (this.isPaused) return;
                const now = Date.now();
                const remaining = Math.max(
                    0,
                    Math.round((this.endAt - now) / 1000)
                );
                this.timerCounter = remaining;

                // Interval bells are scheduled via Web Audio on mount so
                // they fire on the Lock Screen without a banner. No need
                // to fire any in-app bell from the tick loop.

                if (remaining === 0) {
                    this.clearAllIntervals();
                    if (!this.isWatched) {
                        this.playIntervalSound();
                        sessions.setWatched(
                            process.env.VUE_APP_TIMER_SESSION_ID
                        );
                        this.isWatched = true;
                    }
                    this.finishTimer();
                }

                this.saveMeditatingTime();
            },

            async initWebAudioBell() {
                try {
                    const AudioCtx =
                        window.AudioContext || window.webkitAudioContext;
                    if (!AudioCtx) return;
                    if (!this.webAudioCtx) this.webAudioCtx = new AudioCtx();
                    if (this.webAudioCtx.state === 'suspended') {
                        await this.webAudioCtx.resume();
                    }
                    if (!this.bellBuffer) {
                        const resp = await fetch(BELL_URL);
                        const buf = await resp.arrayBuffer();
                        this.bellBuffer = await this.webAudioCtx.decodeAudioData(
                            buf
                        );
                    }
                } catch (err) {
                    console.warn('[timer] web audio init failed', err);
                }
            },

            scheduleIntervalBells(remainingSeconds, intervalMinutes) {
                this.cancelScheduledBells();
                if (!intervalMinutes || intervalMinutes <= 0) return;
                if (!this.webAudioCtx || !this.bellBuffer) return;
                const stepSec = intervalMinutes * 60;
                const stepCount = Math.floor(
                    (remainingSeconds - 1) / stepSec // skip a bell colliding with the end
                );
                const now = this.webAudioCtx.currentTime;
                for (let i = 1; i <= stepCount; i++) {
                    const source = this.webAudioCtx.createBufferSource();
                    source.buffer = this.bellBuffer;
                    source.connect(this.webAudioCtx.destination);
                    source.start(now + i * stepSec);
                    this.scheduledBells.push(source);
                }
            },

            cancelScheduledBells() {
                for (const source of this.scheduledBells) {
                    try {
                        source.stop();
                    } catch (_) {}
                    try {
                        source.disconnect();
                    } catch (_) {}
                }
                this.scheduledBells = [];
            },

            teardownWebAudio() {
                if (!this.webAudioCtx) return;
                try {
                    this.webAudioCtx.close();
                } catch (_) {}
                this.webAudioCtx = null;
                this.bellBuffer = null;
            },

            // Data-URL because iOS's Now Playing widget can't fetch the
            // `capacitor://localhost/img/...` scheme directly.
            async setLockScreenMetadata() {
                if (!('mediaSession' in navigator) || !window.MediaMetadata)
                    return;
                try {
                    const base = process.env.BASE_URL || '/';
                    const resp = await fetch(
                        `${base}img/icons/icon-512x512.png`
                    );
                    const blob = await resp.blob();
                    const dataUrl = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result);
                        reader.onerror = reject;
                        reader.readAsDataURL(blob);
                    });
                    navigator.mediaSession.metadata = new window.MediaMetadata({
                        title: this.$t('timer.title') || 'Meditation Timer',
                        artist: 'Chillanywhere',
                        artwork: [
                            {
                                src: dataUrl,
                                sizes: '512x512',
                                type: 'image/png'
                            }
                        ]
                    });
                } catch (err) {
                    console.warn('[timer] mediaSession metadata failed', err);
                }
            },

            initBackgroundSound() {
                this.backgroundSound = new Audio(this.timerSettings?.soundUrl);
                if (typeof this.backgroundSound.loop == 'boolean') {
                    this.backgroundSound.loop = true;
                } else {
                    this.backgroundSoundListener = this.backgroundSound.addEventListener(
                        'ended',
                        () => this.backgroundSound.play(),
                        false
                    );
                }

                !this.muteSound && this.backgroundSound.play();
            },

            // Stop/Play background sound
            toggleBackgroundSound() {
                if (!this.backgroundSound) return;
                this.muteSound = !this.muteSound;

                return this.muteSound
                    ? this.backgroundSound.pause()
                    : this.backgroundSound.play();
            },

            // Play interval sound (best-effort; only audible in foreground).
            playIntervalSound() {
                if (!this.intervalSound) return;
                try {
                    this.intervalSound.currentTime = 0;
                    this.intervalSound.play();
                } catch (_) {
                    /* autoplay blocked, etc. */
                }
            },

            // Clear defined intervals + stop background audio
            clearAllIntervals() {
                clearInterval(this.timerInterval);
                this.timerInterval = null;

                if (this.backgroundSound) {
                    this.backgroundSound.pause();
                    this.backgroundSound = null;
                }
            },

            // Foreground tick. 250 ms so the displayed seconds update
            // smoothly without jitter.
            initTimer() {
                this.timerInterval = setInterval(() => {
                    this.recomputeFromClock();
                }, 250);
            },

            // Pause/resume the timer — on pause we cancel all scheduled
            // bells; on resume we re-schedule for the remaining time.
            async toggleRunTimer() {
                if (!this.isPaused) {
                    // Pausing
                    this.isPaused = true;
                    this.pausedRemaining = Math.max(
                        0,
                        Math.round((this.endAt - Date.now()) / 1000)
                    );
                    clearInterval(this.timerInterval);
                    this.timerInterval = null;
                    if (this.backgroundSound) this.backgroundSound.pause();
                    this.cancelScheduledBells();
                    await TimerSchedule.cancelTimer();
                    // End the Live Activity on pause — iOS's native timer
                    // element counts down unconditionally, so leaving it
                    // running would misreport the remaining time. A new
                    // activity is started on resume with the fresh endAt.
                    await LiveActivity.end();
                    await AndroidTimerNotification.stop();
                } else {
                    // Resuming with whatever time was left.
                    const intervalMin = this.timerSettings?.interval || 0;
                    this.startedAt = Date.now();
                    const { endAt } = await TimerSchedule.startTimer({
                        totalSeconds: this.pausedRemaining,
                        intervalMinutes: intervalMin,
                        sound: isIOS() ? 'bell.caf' : 'bell'
                    });
                    this.endAt = endAt;
                    this.isPaused = false;
                    this.initTimer();
                    this.scheduleIntervalBells(
                        this.pausedRemaining,
                        intervalMin
                    );
                    if (this.backgroundSound && !this.muteSound) {
                        this.backgroundSound.play();
                    }
                    LiveActivity.start({ endAt, startedAt: this.startedAt });
                    AndroidTimerNotification.start({ endAt });
                }
            },

            // Stop timer and close modal window
            async finishTimer() {
                if (
                    this.moodBefore &&
                    !this.session.todayVote.isSkip &&
                    !this.session.todayVote.isCompleted
                )
                    this.setShowMood();

                // Stop scheduled bells / Live Activity before teardown so the
                // Lock Screen + Dynamic Island clear even if Vue's async
                // beforeDestroy doesn't await our work.
                this.cancelScheduledBells();
                this.clearAllIntervals();
                try {
                    await TimerSchedule.cancelTimer();
                } catch (_) {}
                try {
                    await LiveActivity.end();
                } catch (_) {}
                try {
                    await AndroidTimerNotification.stop();
                } catch (_) {}

                this.toggleBackgroundSound();
                await this.saveMeditatingTime(true);
                await this.writeMindfulSessionToHealth();
                this.timerCallback();
            },

            // Mirror the meditation duration into HealthKit / Health Connect.
            // No-op on the web build and skipped for sub-30s sessions
            // (matches the in-app "watched" threshold so accidental aborts
            // don't pollute the user's mindful-minutes history).
            async writeMindfulSessionToHealth() {
                const totalSec = this.timerSettings?.timer * 60;
                const seconds =
                    this.timeMeditated || totalSec - this.timerCounter || 0;
                if (seconds < 30) return;
                const endDate = new Date();
                const startDate = new Date(endDate.getTime() - seconds * 1000);
                const sessionId = this.sessionViewId
                    ? `timer-view-${this.sessionViewId}`
                    : `timer-${this.startedAt}`;
                try {
                    await this.health.storeMindfulSession({
                        sessionId,
                        startDate,
                        endDate,
                        durationSeconds: seconds,
                        metadata: { source: 'timer' }
                    });
                } catch (err) {
                    console.warn('[timer] health write failed', err);
                }
            },

            /**
             * Save viewed time
             */
            async saveViewSession() {
                // View data for meditation
                let viewData = {
                    time: '0',
                    sessionId: process.env.VUE_APP_TIMER_SESSION_ID,
                    type: 'recorded'
                };

                // Send view data for statistics when set after mood
                if (this.isMember) {
                    const resp = await sessions.saveWatchedTime(viewData);

                    if (resp.ok) this.sessionViewId = resp.data.id;
                }
            },

            // Save watching time. Intermediate ticks fire-and-forget — each
            // call carries an absolute cumulative time, so a dropped tick
            // self-heals on the next one. The final `force` flush has no
            // follow-up, so it awaits with retry to avoid losing minutes
            // on a transient network blip.
            async saveMeditatingTime(force = false) {
                if (
                    !force &&
                    this.timerSettings?.timer * 60 -
                        this.timerCounter -
                        this.timeMeditated <
                        10
                )
                    return;

                this.timeMeditated =
                    this.timerSettings?.timer * 60 - this.timerCounter;

                let viewData = {
                    id: this.sessionViewId,
                    time: this.timeMeditated.toString(),
                    sessionId: process.env.VUE_APP_TIMER_SESSION_ID,
                    type: 'recorded'
                };

                if (!this.isMember) return;

                if (force) {
                    await sessions.updateWatchedTimeWithRetry(viewData);
                } else {
                    sessions.updateWatchedTime(viewData);
                }
            }
        }
    };
</script>

<style lang="stylus" scoped>
    @import "../../../assets/style/_variables.styl"

    .timerControls
        display: flex

        > *
            flex: 1 1 50%
            min-width: auto

    .backgroundVideoOverflow
        z-index: 2
        width: 100vw
        height: 100vh
        position: fixed
        top: 0
        left: 0
        background-size: cover
        background-position: center

        div
            width: 100%
            height: 100%
            background-color: rgba(2, 35, 65, 0.6)

    .backgroundVideo
        z-index: 1
        object-fit: cover
        filter: blur(6px)


    >>> .timerModal
        width: 100vw
        height: 100vh
        max-height: 100vh
        overflow: hidden
        display: flex
        justify-content: center
        align-items: center

    .timerModalInner
        z-index: 2
        width: 320px;
        padding: 30px 15px
        border-radius: 10px

    .timerCountdown
        white-space: nowrap
        font-size: 34px
        font-weight: 300
        position: absolute
        top: 50%
        left: 50%
        transform: translate(-50%, -50%)

    .timerModalInner
    .timer
        display: flex
        flex-direction: column
        align-items: center

    .timerCounter
        width: 280px
        height: 280px
        position: relative
        display: flex
        justify-content: center
        align-items: center
        margin-top: 40px
        padding: 10px
        border-radius: 50%
        background-color: rgba(255, 255, 255, 0.1)

    .toggleBackgroundSound
        z-index: 4
        width: 50px
        height: 50px
        border-radius: 50px
        position: absolute
        left: 0
        top: 0
        display: flex
        align-items: center
        justify-content: center
        border: 2px solid $theme.white
        cursor: pointer

    @media screen and (max-width: $grid-breakpoints.sm - 1)
        .timerModalInner
        .timer
        .timerControls
            width: 100%

        .timerFinish
            font-size: 17px
            padding: 17px 30px
</style>
